import React from 'react';
import {
  FiMap,
  FiMove,
  FiMaximize,
  FiCrosshair,
  FiCircle,
} from 'react-icons/fi';
import {
  Mafs,
  Coordinates,
  Line,
  Circle,
  Text,
  Theme,
  Plot,
  Point,
} from 'mafs';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

// Visual Components
const CoordinatePlaneVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Coordinate Plane with Grid
    </h4>
    <Mafs
      width={400}
      height={350}
      viewBox={{ x: [-5, 5], y: [-4, 4] }}
      darkMode={false}
    >
      <Coordinates.Cartesian />

      {[-4, -3, -2, -1, 1, 2, 3, 4].map((x) => (
        <Plot.OfY
          key={`vline-${x}`}
          x={() => x}
          color="#e5e7eb"
          strokeWidth={1}
          domain={[-4, 4]}
        />
      ))}
      {[-3, -2, -1, 1, 2, 3].map((y) => (
        <Plot.OfX
          key={`hline-${y}`}
          y={() => y}
          color="#e5e7eb"
          strokeWidth={1}
          domain={[-5, 5]}
        />
      ))}

      <Point x={0} y={0} color="#dc2626" />
      <Text x={0.3} y={-0.3} color="#dc2626" size={14} weight="bold">
        (0, 0)
      </Text>
      <Text x={-0.5} y={-0.3} color="#dc2626" size={13}>
        Origin
      </Text>

      <Text x={4.5} y={0.3} color="#1f2937" size={16} weight="bold">
        x
      </Text>
      <Text x={0.3} y={3.5} color="#1f2937" size={16} weight="bold">
        y
      </Text>

      {[-4, -3, -2, -1, 1, 2, 3, 4].map((x) => (
        <Text key={`x-label-${x}`} x={x} y={-0.4} color="#1f2937" size={12}>
          {x}
        </Text>
      ))}
      {[-3, -2, -1, 1, 2, 3].map((y) => (
        <Text key={`y-label-${y}`} x={-0.4} y={y} color="#1f2937" size={12}>
          {y}
        </Text>
      ))}

      <Point x={3} y={2} color="#ef4444" />
      <Text x={3.3} y={2.3} color="#dc2626" size={14} weight="bold">
        (3, 2)
      </Text>

      <Point x={-2} y={3} color="#10b981" />
      <Text x={-2.7} y={3.3} color="#059669" size={14} weight="bold">
        (-2, 3)
      </Text>

      <Point x={-3} y={-2} color="#2563eb" />
      <Text x={-3.7} y={-2.3} color="#1d4ed8" size={14} weight="bold">
        (-3, -2)
      </Text>

      <Point x={2} y={-3} color="#f59e0b" />
      <Text x={2.3} y={-3.3} color="#d97706" size={14} weight="bold">
        (2, -3)
      </Text>

      <Text x={2} y={3} color="#1f2937" size={18} weight="bold">
        I
      </Text>
      <Text x={-2} y={3} color="#1f2937" size={18} weight="bold">
        II
      </Text>
      <Text x={-2} y={-3} color="#1f2937" size={18} weight="bold">
        III
      </Text>
      <Text x={2} y={-3} color="#1f2937" size={18} weight="bold">
        IV
      </Text>

      <Text x={3.5} y={1} color="#1f2937" size={12} weight="bold">
        (+,+)
      </Text>
      <Text x={-3.5} y={1} color="#1f2937" size={12} weight="bold">
        (-,+)
      </Text>
      <Text x={-3.5} y={-1} color="#1f2937" size={12} weight="bold">
        (-,-)
      </Text>
      <Text x={3.5} y={-1} color="#1f2937" size={12} weight="bold">
        (+,-)
      </Text>
    </Mafs>
  </div>
);

const SlopeVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Slope Examples
    </h4>
    <Mafs
      width={400}
      height={300}
      viewBox={{ x: [-5, 5], y: [-3, 3] }}
      darkMode={false}
    >
      <Coordinates.Cartesian />

      <Plot.OfX
        y={(x) => 0.5 * x + 1}
        color="#10b981"
        strokeWidth={2}
        domain={[-4, 4]}
      />
      <Text x={2} y={2.3} color="#059669" size={14} weight="bold">
        m = 1/2
      </Text>

      <Plot.OfX
        y={(x) => -0.8 * x - 1}
        color="#ef4444"
        strokeWidth={2}
        domain={[-3, 3]}
      />
      <Text x={1} y={-2.2} color="#dc2626" size={14} weight="bold">
        m = -4/5
      </Text>

      <Plot.OfX y={() => -2} color="#f59e0b" strokeWidth={2} domain={[-4, 4]} />
      <Text x={3} y={-1.7} color="#d97706" size={14} weight="bold">
        m = 0
      </Text>

      <Plot.OfY x={() => -3} color="#7c3aed" strokeWidth={2} domain={[-2, 2]} />
      <Text x={-2.7} y={1.5} color="#6d28d9" size={14} weight="bold">
        undefined
      </Text>
    </Mafs>
  </div>
);

const DistanceVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Distance Formula
    </h4>
    <Mafs
      width={400}
      height={300}
      viewBox={{ x: [-1, 6], y: [-1, 5] }}
      darkMode={false}
    >
      <Coordinates.Cartesian />

      <Point x={1} y={1} color="#ef4444" />
      <Text x={0.5} y={0.5} color="#dc2626" size={14} weight="bold">
        A(1,1)
      </Text>

      <Point x={4} y={3} color="#ef4444" />
      <Text x={4.2} y={3.3} color="#dc2626" size={14} weight="bold">
        B(4,3)
      </Text>

      <Plot.OfX
        y={() => 1}
        color="#10b981"
        strokeWidth={2}
        strokeDasharray="5,5"
        domain={[1, 4]}
      />
      <Plot.OfY
        x={() => 4}
        color="#10b981"
        strokeWidth={2}
        strokeDasharray="5,5"
        domain={[1, 3]}
      />

      <Plot.OfX
        y={(x) => (2 / 3) * x + 1 / 3}
        color="#2563eb"
        strokeWidth={3}
        domain={[1, 4]}
      />

      <Text x={2.5} y={0.7} color="#059669" size={14} weight="bold">
        Δx = 3
      </Text>
      <Text x={4.3} y={2} color="#059669" size={14} weight="bold">
        Δy = 2
      </Text>
      <Text x={2} y={2.2} color="#1d4ed8" size={14} weight="bold">
        d = √13
      </Text>

      <Text x={0.5} y={4.5} color="#1f2937" size={14} weight="bold">
        d = √[(x₂-x₁)² + (y₂-y₁)²]
      </Text>
      <Text x={0.5} y={4} color="#1f2937" size={13}>
        d = √[(4-1)² + (3-1)²] = √13
      </Text>
    </Mafs>
  </div>
);

const MidpointVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Midpoint Formula
    </h4>
    <Mafs
      width={400}
      height={300}
      viewBox={{ x: [-1, 6], y: [-1, 5] }}
      darkMode={false}
    >
      <Coordinates.Cartesian />

      <Point x={1} y={2} color="#ef4444" />
      <Text x={0.3} y={1.7} color="#dc2626" size={14} weight="bold">
        A(1,2)
      </Text>

      <Point x={5} y={4} color="#ef4444" />
      <Text x={5.2} y={4.3} color="#dc2626" size={14} weight="bold">
        B(5,4)
      </Text>

      <Plot.OfX
        y={(x) => 0.5 * x + 1.5}
        color="#6b7280"
        strokeWidth={2}
        domain={[1, 5]}
      />

      <Point x={3} y={3} color="#10b981" />
      <Text x={3.2} y={3.3} color="#059669" size={14} weight="bold">
        M(3,3)
      </Text>

      <Text x={0.5} y={4.5} color="#1f2937" size={14} weight="bold">
        M = ((x₁+x₂)/2, (y₁+y₂)/2)
      </Text>
      <Text x={0.5} y={4} color="#1f2937" size={13}>
        M = ((1+5)/2, (2+4)/2) = (3,3)
      </Text>
    </Mafs>
  </div>
);

const MidpointFormulaVisual = () => (
  <div style={{ height: '300px', width: '100%', position: 'relative' }}>
    <Mafs>
      <Coordinates.Cartesian />
      <Line.Segment point1={[1, 2]} point2={[5, 4]} color={Theme.red} />
      <Circle center={[1, 2]} radius={0.2} color={Theme.red} />
      <Circle center={[5, 4]} radius={0.2} color={Theme.red} />
      <Circle center={[3, 3]} radius={0.2} color={Theme.green} />
      <Text x={1} y={2.3} attach="s">
        A(1,2)
      </Text>
      <Text x={5} y={4.3} attach="s">
        B(5,4)
      </Text>
      <Text x={3} y={3.3} attach="s">
        M(3,3)
      </Text>
      <Text x={0} y={-4} attach="n" size={18}>
        {
          '§M = (\\frac{x_1+x_2}{2}, \\frac{y_1+y_2}{2}) = (\\frac{1+5}{2}, \\frac{2+4}{2}) = (3,3)§'
        }
      </Text>
    </Mafs>
  </div>
);

const CoordinateGeometryLesson = () => {
  const sections = [
    {
      id: 'coordinate-plane',
      title: 'Coordinate Plane',
      content: {
        introduction:
          'The coordinate plane consists of two perpendicular number lines that intersect at the origin, creating four quadrants.',

        concepts: [
          {
            title: 'Points and Quadrants',
            explanation:
              'Points are located using ordered pairs (x, y). The four quadrants have different sign patterns: I(+,+), II(-,+), III(-,-), IV(+,-).',
            visual: <CoordinatePlaneVisual />,
            example: {
              problem: 'In which quadrant is the point (-3, 5) located?',
              solution: [
                'Identify the signs: x = -3 (negative), y = 5 (positive)',
                'Negative x, positive y corresponds to Quadrant II',
                'The point (-3, 5) is in Quadrant II',
                'Quadrant II contains all points with negative x and positive y',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'distance-formula',
      title: 'Distance Formula',
      content: {
        introduction:
          'The distance between two points can be calculated using the Pythagorean theorem in coordinate form.',

        concepts: [
          {
            title: 'Distance Between Points',
            explanation:
              'Distance §d = \\sqrt{(x₂-x₁)² + (y₂-y₁)²}§. This formula comes from creating a right triangle between the points.',
            visual: <DistanceVisual />,
            example: {
              problem: 'Find the distance between points A(2, 3) and B(6, 6)',
              solution: [
                'Use distance formula: §d = \\sqrt{(x₂-x₁)² + (y₂-y₁)²}§',
                'Substitute coordinates: §d = \\sqrt{(6-2)² + (6-3)²}§',
                'Simplify: §d = \\sqrt{16 + 9} = \\sqrt{25}§',
                'Distance = §5§ units',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'midpoint-formula',
      title: 'Midpoint Formula',
      content: {
        introduction:
          'The midpoint of a line segment is the point that divides the segment into two equal parts.',

        concepts: [
          {
            title: 'Finding Midpoints',
            explanation:
              'The midpoint is the exact center of a line segment. Simply average the x-coordinates and y-coordinates separately. The formula is §M = (\\frac{x_1+x_2}{2}, \\frac{y_1+y_2}{2})§.',
            visual: <MidpointVisual />,
            example: {
              problem:
                'Find the midpoint of the segment connecting (-2, 4) and (6, -2).',
              solution: [
                'Use midpoint formula: §M = (\\frac{x_1+x_2}{2}, \\frac{y_1+y_2}{2})§',
                'Substitute coordinates: §M = (\\frac{-2+6}{2}, \\frac{4+(-2)}{2})§',
                'Simplify: §M = (\\frac{4}{2}, \\frac{2}{2}) = (2, 1)§',
                'Midpoint is (2, 1)',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'slope',
      title: 'Slope & Lines',
      content: {
        introduction:
          "Slope measures the steepness and direction of a line. It's the rate of change between any two points on the line.",

        concepts: [
          {
            title: 'Slope Formula',
            explanation:
              'Slope §m = \\frac{y_2-y_1}{x_2-x_1} = \\frac{\\text{rise}}{\\text{run}}§. Positive slopes go up from left to right, negative slopes go down.',
            visual: <SlopeVisual />,
            example: {
              problem:
                'Find the slope of the line passing through (1, 3) and (5, 11)',
              solution: [
                'Use slope formula: §m = \\frac{y_2-y_1}{x_2-x_1}§',
                'Substitute coordinates: §m = \\frac{11-3}{5-1}§',
                'Simplify: §m = \\frac{8}{4} = 2§',
                'The slope is 2',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question: 'What is the distance between points (0, 0) and (3, 4)?',
      options: ['§5§', '§7§', '§sqrt(7)§', '§12§', '§sqrt(12)§'],
      correct: 0,
      explanation:
        'Distance = §\\sqrt{(3-0)^2 + (4-0)^2} = \\sqrt{9 + 16} = \\sqrt{25} = 5§',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'Find the midpoint of the segment connecting (-1, 2) and (5, 8)',
      options: ['(2, 5)', '(3, 5)', '(4, 6)', '(2, 3)', '(3, 4)'],
      correct: 0,
      explanation:
        'Midpoint = §\\left(\\frac{-1+5}{2}, \\frac{2+8}{2}\\right) = \\left(\\frac{4}{2}, \\frac{10}{2}\\right) = (2, 5)§',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'What is the slope of the line passing through (2, 1) and (6, 9)?',
      options: ['§2§', '§1/2§', '§4§', '§1/4§', '§-2§'],
      correct: 0,
      explanation: 'Slope = §\\frac{9-1}{6-2} = \\frac{8}{4} = 2§',
    },
    {
      type: 'timed',
      timeLimit: 105,
      question:
        'A line passes through (0, 3) with slope -2. What is its equation in slope-intercept form?',
      options: [
        'y = -2x + 3',
        'y = 2x + 3',
        'y = -2x - 3',
        'y = -3x + 2',
        'y = 3x - 2',
      ],
      correct: 0,
      explanation:
        'Using §y = mx + b§ with §m = -2§ and y-intercept = 3: §y = -2x + 3§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'What is the distance between points (-2, 5) and (4, -3)?',
      options: ['10', '8', '12', '6', '14'],
      correct: 0,
      explanation:
        'Distance = §\\sqrt{(4-(-2))² + (-3-5)²} = \\sqrt{36 + 64} = \\sqrt{100} = 10§.',
    },
    {
      type: 'timed',
      timeLimit: 115,
      question:
        'Two lines are perpendicular. If one has slope §3/4§, what is the slope of the other?',
      options: ['-4/3', '4/3', '-3/4', '3/4', '0'],
      correct: 0,
      explanation:
        'Perpendicular slopes are negative reciprocals. Slope = §-1/(3/4) = -4/3§.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question: 'A line has equation 3x - 4y = 12. What is its y-intercept?',
      options: ['-3', '3', '4', '-4', '12'],
      correct: 0,
      explanation: 'Set §x = 0§: §-4y = 12§ → §y = -3§. Y-intercept is §-3§.',
    },
    {
      type: 'timed',
      timeLimit: 125,
      question:
        'Points A(1, 2), B(4, 6), and C(7, 10) are collinear if they lie on the same line. Are they collinear?',
      options: [
        'Yes',
        'No',
        'Cannot determine',
        'Only if extended',
        'Only A and B',
      ],
      correct: 0,
      explanation:
        'Check slopes: §m(AB) = \\frac{6-2}{4-1} = \\frac{4}{3}§. §m(BC) = \\frac{10-6}{7-4} = \\frac{4}{3}§. Same slope, so yes, collinear.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'In which quadrant is the point (-4, -7) located?',
      options: ['III', 'I', 'II', 'IV', 'On an axis'],
      correct: 0,
      explanation:
        'Both coordinates are negative, so the point is in Quadrant III',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'If a line has slope 0, what type of line is it?',
      options: [
        'Horizontal',
        'Vertical',
        'Diagonal',
        'Curved',
        'None of these',
      ],
      correct: 0,
      explanation:
        'A slope of 0 means no vertical change, creating a horizontal line',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'Two lines are parallel. If one has slope 5, what is the slope of the other?',
      options: ['5', '-5', '1/5', '-1/5', 'Cannot determine'],
      correct: 0,
      explanation: 'Parallel lines have equal slopes. §Slope = 5§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'What is the midpoint between (0, 0) and (8, 12)?',
      options: ['(4, 6)', '(8, 12)', '(2, 3)', '(6, 9)', '(4, 8)'],
      correct: 0,
      explanation:
        'Midpoint = §\\left(\\frac{0+8}{2}, \\frac{0+12}{2}\\right) = (4, 6)§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'A line passes through (1, 5) and (3, 9). What is its slope?',
      options: ['2', '4', '1/2', '-2', '3'],
      correct: 0,
      explanation: 'Slope = §\\frac{9-5}{3-1} = \\frac{4}{2} = 2§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'If point M(x, 4) is the midpoint of A(2, 6) and B(8, 2), what is x?',
      options: ['5', '4', '6', '3', '7'],
      correct: 0,
      explanation: 'Midpoint x-coordinate: §(2+8)/2 = 10/2 = 5§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A rectangle has vertices at (0,0), (6,0), (6,4), and (0,4). What is its area?',
      options: ['24', '20', '12', '16', '28'],
      correct: 0,
      explanation: 'Length = 6, Width = 4. Area = §6 \\times 4 = 24§.',
    },

    {
      type: 'untimed',
      timeLimit: 90,
      question: 'What is the distance between points (0, 0) and (3, 4)?',
      options: ['§5§', '§7§', '§sqrt(7)§', '§12§', '§sqrt(12)§'],
      correct: 0,
      explanation:
        'Distance = §\\sqrt{(3-0)^2 + (4-0)^2} = \\sqrt{9 + 16} = \\sqrt{25} = 5§',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question:
        'Find the midpoint of the segment connecting (-1, 2) and (5, 8)',
      options: ['(2, 5)', '(3, 5)', '(4, 6)', '(2, 3)', '(3, 4)'],
      correct: 0,
      explanation:
        'Midpoint = §\\left(\\frac{-1+5}{2}, \\frac{2+8}{2}\\right) = \\left(\\frac{4}{2}, \\frac{10}{2}\\right) = (2, 5)§',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'What is the slope of the line passing through (2, 1) and (6, 9)?',
      options: ['§2§', '§1/2§', '§4§', '§1/4§', '§-2§'],
      correct: 0,
      explanation: 'Slope = §\\frac{9-1}{6-2} = \\frac{8}{4} = 2§',
    },
    {
      type: 'untimed',
      timeLimit: 105,
      question:
        'A line passes through (0, 3) with slope -2. What is its equation in slope-intercept form?',
      options: [
        'y = -2x + 3',
        'y = 2x + 3',
        'y = -2x - 3',
        'y = -3x + 2',
        'y = 3x - 2',
      ],
      correct: 0,
      explanation:
        'Using §y = mx + b§ with §m = -2§ and y-intercept = 3: §y = -2x + 3§.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question: 'What is the distance between points (-2, 5) and (4, -3)?',
      options: ['10', '8', '12', '6', '14'],
      correct: 0,
      explanation:
        'Distance = §\\sqrt{(4-(-2))² + (-3-5)²} = \\sqrt{36 + 64} = \\sqrt{100} = 10§.',
    },
    {
      type: 'untimed',
      timeLimit: 115,
      question:
        'Two lines are perpendicular. If one has slope §3/4§, what is the slope of the other?',
      options: ['-4/3', '4/3', '-3/4', '3/4', '0'],
      correct: 0,
      explanation:
        'Perpendicular slopes are negative reciprocals. Slope = §-1/(3/4) = -4/3§.',
    },
    {
      type: 'untimed',
      timeLimit: 130,
      question: 'A line has equation 3x - 4y = 12. What is its y-intercept?',
      options: ['-3', '3', '4', '-4', '12'],
      correct: 0,
      explanation: 'Set §x = 0§: §-4y = 12§ → §y = -3§. Y-intercept is §-3§.',
    },
    {
      type: 'untimed',
      timeLimit: 125,
      question:
        'Points A(1, 2), B(4, 6), and C(7, 10) are collinear if they lie on the same line. Are they collinear?',
      options: [
        'Yes',
        'No',
        'Cannot determine',
        'Only if extended',
        'Only A and B',
      ],
      correct: 0,
      explanation:
        'Check slopes: §m(AB) = \\frac{6-2}{4-1} = \\frac{4}{3}§. §m(BC) = \\frac{10-6}{7-4} = \\frac{4}{3}§. Same slope, so yes, collinear.',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question: 'In which quadrant is the point (-4, -7) located?',
      options: ['III', 'I', 'II', 'IV', 'On an axis'],
      correct: 0,
      explanation:
        'Both coordinates are negative, so the point is in Quadrant III',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question: 'If a line has slope 0, what type of line is it?',
      options: [
        'Horizontal',
        'Vertical',
        'Diagonal',
        'Curved',
        'None of these',
      ],
      correct: 0,
      explanation:
        'A slope of 0 means no vertical change, creating a horizontal line',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'Two lines are parallel. If one has slope 5, what is the slope of the other?',
      options: ['5', '-5', '1/5', '-1/5', 'Cannot determine'],
      correct: 0,
      explanation: 'Parallel lines have equal slopes. §Slope = 5§.',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question: 'What is the midpoint between (0, 0) and (8, 12)?',
      options: ['(4, 6)', '(8, 12)', '(2, 3)', '(6, 9)', '(4, 8)'],
      correct: 0,
      explanation:
        'Midpoint = §\\left(\\frac{0+8}{2}, \\frac{0+12}{2}\\right) = (4, 6)§.',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question: 'A line passes through (1, 5) and (3, 9). What is its slope?',
      options: ['2', '4', '1/2', '-2', '3'],
      correct: 0,
      explanation: 'Slope = §\\frac{9-5}{3-1} = \\frac{4}{2} = 2§.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'If point M(x, 4) is the midpoint of A(2, 6) and B(8, 2), what is x?',
      options: ['5', '4', '6', '3', '7'],
      correct: 0,
      explanation: 'Midpoint x-coordinate: §(2+8)/2 = 10/2 = 5§.',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'A rectangle has vertices at (0,0), (6,0), (6,4), and (0,4). What is its area?',
      options: ['24', '20', '12', '16', '28'],
      correct: 0,
      explanation: 'Length = 6, Width = 4. Area = §6 \\times 4 = 24§.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question:
        'Line L has slope 3. Line M has slope -2. Compare the two quantities.',
      quantityA: 'Slope of L',
      quantityB: 'Slope of M',
      correct: 0,
      explanation:
        'Compare the two quantities directly. Quantity A is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'Which points lie on the line §y = 2x + 1§? Select all that apply.',
      options: ['§(0,1)§', '§(1,3)§', '§(2,4)§', '§(-1,-1)§', '§(3,8)§'],
      correct: [0, 1, 3],
      explanation: 'Substitute each point. A, B, and D satisfy §y=2x+1§.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'Enter the distance between §(0,0)§ and §(6,8)§.',
      correctAnswer: '10',
      acceptedAnswers: ['10'],
      explanation: 'Distance is §sqrt(6^2+8^2)=sqrt(100)=10§.',
    },
  ];

  const quickReference = [
    'Distance: §d = \\sqrt{(x_2-x_1)^2 + (y_2-y_1)^2}§',
    'Midpoint: §M = (\\frac{x_1+x_2}{2}, \\frac{y_1+y_2}{2})§',
    'Slope: §m = \\frac{y_2-y_1}{x_2-x_1}§',
    'Parallel lines: same slope',
    'Perpendicular lines: §m_1 \\times m_2 = -1§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Coordinate Geometry?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Coordinate Geometry concepts for the GRE Quantitative section',
    aicontext:
      'Master the coordinate plane, distance formula, midpoint formula, and slope calculations',
  };

  return (
    <LessonTemplate
      lessonId="coordinate-geometry"
      lessonTitle="Coordinate Geometry"
      lessonIcon={<FiMap />}
      lessonDescription="Master points, lines, slopes, distances, and shapes on the coordinate plane"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="coordinate-geometry" />
      }
    />
  );
};

export default CoordinateGeometryLesson;
